import * as THREE from 'three';
import { EW, SIM } from '../config/tuning';
import type { Checksum, Checksummable } from '../core/Checksum';
import type { ThreatPlacement } from '../config/threats';
import type { Terrain } from '../world/Terrain';

/**
 * Electronic warfare (SPEC §3.5.4).
 *
 * Jammers are area denial: nothing to shoot at, nothing to shoot back with, just a region of the
 * map where the aircraft stops being able to trust its instruments. Three effects, all driven by
 * one severity number:
 *
 *  - **Datalink degradation.** Link quality falls, which drives the compression and jitter of the
 *    post stack (SPEC §3.3.2 item 6). A picture breaking up is the *only* warning the player gets
 *    that they are being jammed, which is why M4 built the artefacts before this existed to drive
 *    them.
 *  - **GPS denial.** Waypoint and threat positions drift, at up to 15 m/s of accumulated error.
 *    Navigation falls back to the coastline and the terrain.
 *  - **Total link loss.** Below a threshold the drone flies its last commanded vector and the
 *    player has no input at all. Capped at 4 s, because it is terrifying and would stop being
 *    interesting if it lasted longer.
 *
 * The drift is closed-form in accumulated time rather than a random walk: the same flight through
 * the same jammer produces the same error, which is what makes a replay reproduce and a bug
 * report reproducible (SPEC §4.3).
 *
 * Control latency under jamming (§3.5.4) is **deliberately not implemented here**. The spec puts
 * it in the final third of the campaign, so it belongs with the difficulty modifiers the mission
 * files turn on at M8, not switched on globally at M5.
 *
 * Units: metres, seconds, m/s.
 */

export interface Jammer {
  designation: string;
  readonly positionM: THREE.Vector3;
  radiusM: number;
}

/** Where the control station sits. Link quality falls with range from it (SPEC §3.3.2). */
export const CONTROL_STATION_M = new THREE.Vector3(0, 0, 0);

const toJammer = new THREE.Vector3();
const stationScratch = new THREE.Vector3();

/**
 * Datalink quality from geometry alone, 0–1: range to the control station, cut when terrain masks
 * the line to it. Jamming multiplies into this rather than replacing it.
 *
 * Pure, so the link budget can be tested without a simulation — and separated from the jamming
 * term so that a player who loses signal can, in principle, tell the two causes apart: dropping
 * into a valley costs a fixed factor, whereas flying into a jammer degrades continuously.
 */
export function datalinkQuality(positionM: THREE.Vector3, terrain: Terrain): number {
  const rangeM = positionM.distanceTo(CONTROL_STATION_M);
  let quality = 1 - rangeM / EW.jammerRadiusM;

  stationScratch.copy(CONTROL_STATION_M);
  stationScratch.y =
    terrain.surfaceHeightAt(CONTROL_STATION_M.x, CONTROL_STATION_M.z) + EW.stationMastHeightM;
  if (!terrain.lineOfSight(stationScratch, positionM)) quality *= EW.occludedLinkFactor;

  return quality < 0 ? 0 : quality > 1 ? 1 : quality;
}

export class EwField implements Checksummable {
  readonly jammers: Jammer[] = [];

  /** Accumulated GPS error, metres, in the world XZ plane. Added to every waypoint and tag. */
  readonly gpsErrorM = new THREE.Vector2();

  /** 0 clean, 1 fully jammed. Drives HUD degradation and the link. */
  jamming = 0;
  /** Datalink quality after geometry and jamming, 0–1. One number, read by the HUD and the post stack. */
  linkQuality = 1;
  /** True while the datalink is down entirely and the aircraft is flying itself. */
  linkLost = false;

  private linkLostRemainingS = 0;
  private driftPhaseS = 0;
  /** The geometry term, refreshed at the detection rate rather than every tick. */
  private baseLinkQuality = 1;
  private linkRefreshS = 0;

  /** Place the jammers of a mission (or the M5 demo layout) onto the terrain. */
  add(placement: ThreatPlacement, terrain: Terrain, radiusM: number = EW.jammerRadiusM): void {
    const groundY = terrain.surfaceHeightAt(placement.xM, placement.zM);
    this.jammers.push({
      designation: placement.designation,
      positionM: new THREE.Vector3(placement.xM, groundY + EW.stationMastHeightM, placement.zM),
      radiusM,
    });
  }

  /** Drop every jammer, so a new mission starts from an empty theatre. */
  clear(): void {
    this.jammers.length = 0;
    this.reset();
  }

  reset(): void {
    this.gpsErrorM.set(0, 0);
    this.jamming = 0;
    this.linkQuality = 1;
    this.linkLost = false;
    this.linkLostRemainingS = 0;
    this.driftPhaseS = 0;
    this.baseLinkQuality = 1;
    this.linkRefreshS = 0;
  }

  /**
   * Jamming severity at a world position, 0–1.
   *
   * Falls off as `(1 − d/r)^exp` with an exponent above 1, giving a soft edge and a hard core.
   * That shape is what makes clipping the fringe of a jammer a decision with a cost rather than a
   * binary in-or-out, and it means a player can feel themselves entering one.
   */
  severityAt(positionM: THREE.Vector3): number {
    let worst = 0;
    for (let i = 0; i < this.jammers.length; i += 1) {
      const jammer = this.jammers[i];
      toJammer.copy(jammer.positionM).sub(positionM);
      const distanceM = toJammer.length();
      if (distanceM >= jammer.radiusM) continue;
      const severity = (1 - distanceM / jammer.radiusM) ** EW.jammerFalloffExp;
      if (severity > worst) worst = severity;
    }
    return worst;
  }

  /**
   * Advance the EW state for one simulation tick.
   *
   * Jamming multiplies into the geometric link budget rather than replacing it, so flying into a
   * valley inside a jammer zone is worse than either alone — the intended geometry of the late
   * campaign. The geometry term costs a terrain ray march, so it is refreshed at the detection
   * rate; jamming and the drift integrate every tick, where they are nearly free.
   */
  tick(dtS: number, positionM: THREE.Vector3, terrain: Terrain): number {
    this.jamming = this.severityAt(positionM);
    this.driftPhaseS += dtS;

    this.linkRefreshS -= dtS;
    if (this.linkRefreshS <= 0) {
      this.linkRefreshS = 1 / SIM.DETECTION_HZ;
      this.baseLinkQuality = datalinkQuality(positionM, terrain);
    }

    const linkQuality = this.baseLinkQuality * this.jammedLinkFactor();
    this.linkQuality = linkQuality;

    // GPS: error accumulates while jammed and is walked off once the receiver can see satellites
    // again. The direction turns slowly, so the error wanders rather than jitters.
    if (this.jamming > 0) {
      const rateMs = EW.gpsDriftMaxMs * this.jamming;
      const angle = this.driftPhaseS * EW.gpsDriftTurnHz * Math.PI * 2;
      this.gpsErrorM.x += Math.cos(angle) * rateMs * dtS;
      this.gpsErrorM.y += Math.sin(angle) * rateMs * dtS;
    } else if (this.gpsErrorM.x !== 0 || this.gpsErrorM.y !== 0) {
      const errorM = this.gpsErrorM.length();
      const recoveredM = errorM - EW.gpsRecoveryMs * dtS;
      if (recoveredM <= 0) this.gpsErrorM.set(0, 0);
      else this.gpsErrorM.multiplyScalar(recoveredM / errorM);
    }

    // Total link loss, bounded. The cap is why this is a moment rather than a punishment.
    if (this.linkLostRemainingS > 0) {
      this.linkLostRemainingS -= dtS;
      if (this.linkLostRemainingS <= 0) this.linkLostRemainingS = 0;
    } else if (linkQuality < EW.linkLossThreshold) {
      this.linkLostRemainingS = EW.linkLossMaxS;
    }
    this.linkLost = this.linkLostRemainingS > 0;

    return linkQuality;
  }

  checksum(into: Checksum): void {
    into.addFloat(this.jamming);
    into.addFloat(this.gpsErrorM.x);
    into.addFloat(this.gpsErrorM.y);
    into.addFloat(this.linkLostRemainingS);
  }

  /** Link multiplier from jamming alone, 1 clean down to `jammedLinkFloor` at the core. */
  private jammedLinkFactor(): number {
    if (this.jamming <= 0) return 1;
    return 1 - (1 - EW.jammedLinkFloor) * this.jamming;
  }
}
